import React, { useState, useEffect, useRef } from 'react';
import { FaGraduationCap, FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSearch, FaBook, FaChevronDown } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as masterService from '../services/masterService';

const CreateStream = () => {
    const [streams, setStreams] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        subjectId: ''
    });
    const [editingId, setEditingId] = useState(null);
    const [toast, setToast] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Search & Dropdown State
    const [searchSubject, setSearchSubject] = useState('');
    const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
    const subjectRef = useRef(null);

    useEffect(() => {
        // Click outside listener
        const handleClickOutside = (event) => {
            if (subjectRef.current && !subjectRef.current.contains(event.target)) {
                setShowSubjectDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setIsLoading(true);
            const [streamsData, subjectsData] = await Promise.all([
                masterService.getAllStreams(),
                masterService.getAllSubjects()
            ]);

            if (streamsData && streamsData.success && Array.isArray(streamsData.data)) {
                setStreams(streamsData.data);
            } else if (Array.isArray(streamsData)) {
                setStreams(streamsData);
            } else {
                setStreams([]);
            }

            if (subjectsData && subjectsData.success && Array.isArray(subjectsData.data)) {
                setSubjects(subjectsData.data);
            } else if (Array.isArray(subjectsData)) {
                setSubjects(subjectsData);
            } else {
                setSubjects([]);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showToast(error.message || 'Failed to fetch data', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchStreams = async () => {
        try {
            const response = await masterService.getAllStreams();
            if (response && response.success && Array.isArray(response.data)) {
                setStreams(response.data);
            } else if (Array.isArray(response)) {
                setStreams(response);
            } else {
                setStreams([]);
            }
        } catch (error) {
            console.error('Error fetching streams:', error);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim() || !formData.subjectId) {
            showToast('Please fill in all required fields', 'error');
            return;
        }

        const payload = {
            name: formData.name,
            subject_id: formData.subjectId
        };

        try {
            setIsLoading(true);
            if (editingId) {
                await masterService.updateStream(editingId, payload);
                showToast('Stream updated successfully', 'success');
                setEditingId(null);
            } else {
                await masterService.createStream(payload);
                showToast('Stream created successfully', 'success');
            }

            setFormData({ name: '', subjectId: '' });
            setSearchSubject('');
            await fetchStreams();

        } catch (error) {
            console.error('Error saving stream:', error);
            showToast(error.message || 'Failed to save stream', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (stream) => {
        // Handle different possible structures for subject_id
        const subjectId = stream.subject_id || (stream.subject ? stream.subject.id : '') || '';

        setFormData({
            name: stream.name,
            subjectId: subjectId
        });
        const subjectName = getSubjectName(subjectId);
        setSearchSubject(subjectName === 'Unknown Department' ? '' : subjectName);
        setEditingId(stream.id);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this stream?')) {
            try {
                setIsLoading(true);
                await masterService.deleteStream(id);
                showToast('Stream deleted successfully', 'success');
                await fetchStreams();
            } catch (error) {
                console.error('Error deleting stream:', error);
                showToast(error.message || 'Failed to delete stream', 'error');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData({
            name: '',
            subjectId: ''
        });
        setSearchSubject('');
    };

    const getSubjectName = (id) => {
        const subject = subjects.find(s => s.id === id || s._id === id);
        return subject ? subject.name : 'Unknown Department';
    };

    const filteredStreams = streams.filter(stream =>
        stream.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getSubjectName(stream.subject_id).toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filtered subjects for search dropdown
    const filteredSubjects = subjects.filter(subject =>
        subject.name.toLowerCase().includes(searchSubject.toLowerCase())
    );

    const darkGradient = {
        background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-8 px-4">
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            <div className="max-w mx-auto">
                {/* Header */}
                <div className="rounded-2xl shadow-xl p-6 mb-8" style={darkGradient}>
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                            <FaGraduationCap className="text-white text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-white">Stream Management</h1>
                            <p className="text-blue-100 mt-1">Create and manage academic streams linked to departments</p>
                        </div>
                    </div>
                </div>

                {/* Form Section */}
                <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <FaPlus className="text-purple-500" />
                        {editingId ? 'Edit Stream' : 'Add New Stream'}
                    </h2>

                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                            {/* Department Selection */}
                            {/* Department Selection (Search & Select) */}
                            <div ref={subjectRef}>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Department <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchSubject}
                                        onChange={(e) => {
                                            setSearchSubject(e.target.value);
                                            setShowSubjectDropdown(true);
                                        }}
                                        onFocus={() => setShowSubjectDropdown(true)}
                                        placeholder="Search or select department..."
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all pr-10"
                                        disabled={isLoading}
                                    />
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 pointer-events-none">
                                        <FaChevronDown className="text-gray-400" />
                                    </div>

                                    {/* Dropdown */}
                                    {showSubjectDropdown && filteredSubjects.length > 0 && (
                                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                            {filteredSubjects.map(subject => (
                                                <button
                                                    key={subject.id}
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, subjectId: subject.id }));
                                                        setSearchSubject(subject.name);
                                                        setShowSubjectDropdown(false);
                                                    }}
                                                    className="w-full px-4 py-3 text-left hover:bg-purple-50 border-b border-gray-100 last:border-b-0 flex items-center gap-2 transition-colors"
                                                >
                                                    <FaBook className="text-purple-500" />
                                                    <span className="text-gray-700 font-medium">{subject.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/* Selected Indicator */}
                                {formData.subjectId && (
                                    <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                                        <span>✓ Selected: {getSubjectName(formData.subjectId)}</span>
                                    </div>
                                )}
                            </div>

                            {/* Stream Name */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Stream Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Software Engineering"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            {editingId && (
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    disabled={isLoading}
                                    className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all shadow-md"
                                >
                                    <FaTimes />
                                    Cancel
                                </button>
                            )}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
                            >
                                {isLoading ? (
                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                ) : (
                                    <FaSave />
                                )}
                                {editingId ? 'Update Stream' : 'Save Stream'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* List Section */}
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
                        <h2 className="text-2xl font-bold text-gray-900">
                            All Streams ({streams.length})
                        </h2>

                        <div className="relative">
                            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search streams..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all w-full md:w-80"
                            />
                        </div>
                    </div>

                    {isLoading && streams.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent mx-auto mb-4"></div>
                            <p className="text-gray-500 text-lg">Loading streams...</p>
                        </div>
                    ) : filteredStreams.length === 0 ? (
                        <div className="text-center py-12">
                            <FaGraduationCap className="text-6xl text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg">
                                {searchTerm ? 'No streams found matching your search' : 'No streams created yet'}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr style={darkGradient}>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-white">S.No</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Department</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Stream Name</th>
                                        <th className="px-6 py-4 text-center text-sm font-bold text-white">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredStreams.map((stream, index) => (
                                        <tr key={stream.id} className="hover:bg-purple-50/50 transition-colors">
                                            <td className="px-6 py-4 text-sm text-gray-900">{index + 1}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                                                    {getSubjectName(stream.subject_id || (stream.subject ? stream.subject.id : ''))}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900 font-medium">{stream.name}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => handleEdit(stream)}
                                                        className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-all"
                                                        title="Edit"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(stream.id)}
                                                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                                                        title="Delete"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateStream;